import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faToggleOn, faToggleOff, faSearch, faFilter, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import BackButton from './BackToDashboardButton';
import http from '../services/httpClient';
import UnitMappingService from '../services/unitMappingService';
import './ExpiryControlPanel.css';

/**
 * Expiry Control Panel.
 *
 * ⚠ CURRENTLY UNREACHABLE. It is mounted at App.js `screen === 'expiry-control'`, but no
 * goTo/setScreen call anywhere in the app produces that screen name. The screen users
 * actually reach is Expiry Thresholds, via the "Expiry" button in Stock In.
 *
 * It used to be worse than unreachable: it was the ONLY writer of ItemExpiryTracking,
 * which was the ONLY table Stock-In enforcement read — so the toggles here went to a table
 * the visible screen never wrote, and the toggles on the visible screen went to a table
 * enforcement never read. Expiry was therefore never enforced for anyone.
 *
 * The endpoints behind this panel now read and write inventory_item_expiry_thresholds,
 * the same single table as everything else, so if it is ever wired up it will agree with
 * the rest of the app rather than fork it again. Deleting it is a product decision, not a
 * correctness one.
 */
const ExpiryControlPanel = ({ goBack }) => {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // all, enabled, disabled, auto
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [savingItems, setSavingItems] = useState(new Set());
  const searchInputRef = useRef(null);

  useEffect(() => {
    fetchMasterList();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [items, searchTerm, filterMode, selectedCategory]);

  const fetchMasterList = async () => {
    try {
      setLoading(true);
      const businessId = UnitMappingService.getBusinessId();
      const result = await http.get(`/expiry-tracking/master-list?businessId=${businessId}`);
      
      if (result.success) {
        setItems(result.items);
        setSummary(result.summary);
        
        // Extract unique categories
        const uniqueCategories = [...new Set(result.items.map(item => item.category).filter(Boolean))];
        setCategories(uniqueCategories.sort());
      }
    } catch (error) {
      console.error('Error fetching expiry master list:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...items];

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.item_name.toLowerCase().includes(search) ||
        (item.category || '').toLowerCase().includes(search)
      );
    }

    // Toggle filter
    if (filterMode === 'enabled') {
      filtered = filtered.filter(item => item.requires_expiry);
    } else if (filterMode === 'disabled') {
      filtered = filtered.filter(item => !item.requires_expiry);
    } else if (filterMode === 'auto') {
      filtered = filtered.filter(item => item.was_auto_enabled);
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    setFilteredItems(filtered);
  };

  const handleToggle = async (itemId, currentValue) => {
    const newValue = !currentValue;
    
    setSavingItems(prev => new Set([...prev, itemId]));
    
    try {
      const businessId = UnitMappingService.getBusinessId();
      const result = await http.post('/expiry-tracking/toggle', {
        itemId,
        requiresExpiry: newValue,
        businessId
      });
      
      if (result.success) {
        // Update local state
        setItems(prev => prev.map(item => 
          item.item_id === itemId 
            ? { ...item, requires_expiry: newValue, manually_toggled_date: new Date().toISOString() }
            : item
        ));
        
        // Update summary
        setSummary(prev => ({
          ...prev,
          tracking_enabled: prev.tracking_enabled + (newValue ? 1 : -1),
          tracking_disabled: prev.tracking_disabled + (newValue ? -1 : 1)
        }));
      }
    } catch (error) {
      console.error('Error toggling expiry tracking:', error);
    } finally {
      setSavingItems(prev => {
        const updated = new Set(prev);
        updated.delete(itemId);
        return updated;
      });
    }
  };

  const handleBulkToggleCategory = async (category, enable) => {
    if (!window.confirm(`${enable ? 'Enable' : 'Disable'} expiry tracking for all items in category "${category}"?`)) {
      return;
    }

    try {
      const businessId = UnitMappingService.getBusinessId();
      const result = await http.post('/expiry-tracking/toggle-category', {
        category,
        requiresExpiry: enable,
        businessId
      });
      
      if (result.success) {
        // Refresh the list
        await fetchMasterList();
      }
    } catch (error) {
      console.error('Error bulk toggling category:', error);
    }
  };

  if (loading) {
    return (
      <div className="expiry-control-panel loading">
        <div className="loader">Loading...</div>
      </div>
    );
  }

  return (
    <div className="expiry-control-panel">
      <header className="panel-header">
        <BackButton goTo={goBack} />
        
        <div className="header-content">
          <h1>Expiry Date Tracking Settings</h1>
          <p>Toggle which items require expiry date entry during stock-in</p>
        </div>

        {summary && (
          <div className="summary-stats">
            <div className="stat">
              <span className="stat-value">{summary.total}</span>
              <span className="stat-label">Total Items</span>
            </div>
            <div className="stat enabled">
              <span className="stat-value">{summary.tracking_enabled}</span>
              <span className="stat-label">Tracking ON</span>
            </div>
            <div className="stat disabled">
              <span className="stat-value">{summary.tracking_disabled}</span>
              <span className="stat-label">Tracking OFF</span>
            </div>
            <div className="stat auto">
              <span className="stat-value">{summary.auto_enabled}</span>
              <span className="stat-label">Auto-Enabled</span>
            </div>
          </div>
        )}
      </header>

      <div className="controls-bar">
        <div className="search-box">
          <FontAwesomeIcon icon={faSearch} />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-controls">
          <select 
            value={filterMode} 
            onChange={(e) => setFilterMode(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Items</option>
            <option value="enabled">Tracking Enabled</option>
            <option value="disabled">Tracking Disabled</option>
            <option value="auto">Auto-Enabled</option>
          </select>

          <select 
            value={selectedCategory} 
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {selectedCategory !== 'all' && (
        <div className="bulk-actions">
          <button 
            className="bulk-action-btn enable"
            onClick={() => handleBulkToggleCategory(selectedCategory, true)}
          >
            Enable All in Category
          </button>
          <button 
            className="bulk-action-btn disable"
            onClick={() => handleBulkToggleCategory(selectedCategory, false)}
          >
            Disable All in Category
          </button>
        </div>
      )}

      <div className="items-list">
        <div className="list-header">
          <span className="col-name">Item Name</span>
          <span className="col-category">Category</span>
          <span className="col-status">Status</span>
          <span className="col-toggle">Tracking</span>
        </div>

        {filteredItems.length === 0 ? (
          <div className="empty-state">
            <p>No Items Found Matching Your Filters</p>
          </div>
        ) : (
          filteredItems.map(item => (
            <div 
              key={item.item_id} 
              className={`item-row ${item.requires_expiry ? 'enabled' : 'disabled'} ${savingItems.has(item.item_id) ? 'saving' : ''}`}
            >
              <span className="col-name">{item.item_name}</span>
              <span className="col-category">{item.category || 'Uncategorized'}</span>
              <span className="col-status">
                {item.was_auto_enabled && (
                  <span className="badge auto-enabled">Auto-Enabled</span>
                )}
                {item.manually_toggled_date && (
                  <span className="badge manually-set">Manual</span>
                )}
                {item.last_expiry_entry_date && (
                  <span className="last-entry">
                    Last: {new Date(item.last_expiry_entry_date).toLocaleDateString()}
                  </span>
                )}
              </span>
              <span className="col-toggle">
                <label className="toggle-switch">
                  <input 
                    type="checkbox" 
                    checked={item.requires_expiry}
                    onChange={() => handleToggle(item.item_id, item.requires_expiry)}
                    disabled={savingItems.has(item.item_id)}
                  />
                  <span className="slider">
                    <FontAwesomeIcon 
                      icon={item.requires_expiry ? faToggleOn : faToggleOff} 
                      className="toggle-icon"
                    />
                  </span>
                </label>
                {savingItems.has(item.item_id) && (
                  <span className="saving-indicator">Saving...</span>
                )}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ExpiryControlPanel;
